/**
 * Oracle API contract — frontend.
 *
 * MIRROR: keep in sync with functions/contracts/oracleApi.js
 * Any change to a shape, code, or taxonomy list MUST update both copies in the same commit.
 *
 * TAXONOMY: the scored unit is now the doc-driven SUBCATEGORY (see taxonomy.js).
 * Gemini scores each subcategory 0-100; the backend computes category rollups,
 * the overall, top_improvements, and the assembled routine. The pre-taxonomy
 * 8-"vector" shape is retained as LEGACY_* for rendering frozen old scans.
 *
 * @typedef {Object} CategoryScore
 * @property {string} key      // taxonomy CATEGORY key
 * @property {number} score    // 0-100 rollup
 * @property {string} summary  // category-level guidance (best lever / plan)
 *
 * @typedef {Object} SubcategoryScore
 * @property {string} key            // taxonomy SUBCATEGORY key
 * @property {number|null} score     // 0-100, or null when not visible (N/A)
 * @property {string} note           // one-line explanation for the score
 * // Optional per-concern classification (product-driving subcats only; catalog
 * // Part 1.2). Absent on legacy scans / when the model didn't classify.
 * @property {string} [active]                       // chosen mechanism ∈ this subcat's regimen_tags
 * @property {("none"|"mild"|"moderate"|"severe")} [severity_band]
 * @property {boolean} [condition_flag]              // reads as a medical condition → clinician
 * @property {boolean} [behavioral_flag]             // best lever is a habit, not a product
 *
 * @typedef {Object} Verdict
 * @property {number} overall_score              // int 1-100 (weighted rollup + reported-use bonus)
 * @property {number} [overall_score_measured]   // the CALIBRATED number, bonus excluded
 * @property {number} [adherence_bonus]          // 0-10, from reported product use
 * @property {CategoryScore[]} categories        // one per taxonomy category
 * @property {SubcategoryScore[]} subcategories  // one per taxonomy subcategory
 * @property {string[]} top_improvements         // product-driving subcat keys, priority order
 *
 * @typedef {Object} RoutineActive
 * @property {string} active_id
 * @property {string} active            // "Retinoid"
 * @property {string} step              // "TREAT"
 * @property {("AM"|"PM")} use_time
 * @property {string} concern
 * @property {("mod"|"partial"|"base")} cls
 * @property {string} treats            // "Texture & congestion"
 * @property {string} timeline
 * @property {string} rung_name
 * @property {number} rung_index        // 0-based current rung
 * @property {number} rung_count
 * @property {boolean} at_ceiling
 * @property {boolean} rx               // current rung routes to a clinician
 * @property {string} product
 * @property {string} brand
 * @property {string} price
 * @property {string} affiliate_url
 * @property {string} image_url         // product thumbnail (catalog hero)
 * @property {string[]} [tags]          // a few clean display tags
 * @property {number|null} [rating]     // catalog star rating
 * @property {number|null} [review_count]
 * @property {("yes"|"partial"|"no"|null)} adherence
 * @property {("product"|"clinician"|"behavioral")} [route]  // delivery; defaults to "product"
 * @property {string} [coaching]        // copy for clinician/behavioral cards (no product)
 *
 * @typedef {Object} Routine
 * @property {RoutineActive[]} am
 * @property {RoutineActive[]} pm
 * @property {string} total_price
 * @property {number} stage            // 0-based progression stage
 *
 * @typedef {Object} VerdictResponse
 * @property {"success"} status
 * @property {string} scan_id
 * @property {string} timestamp
 * @property {string} processed_image_url
 * @property {Verdict} verdict
 * @property {Routine} routine
 * @property {number|null} learned_percentile
 * @property {GeometryReport|null} geometry
 *
 * GeometryReport / GeometryMetric typedefs unchanged (see geometry section below).
 *
 * @typedef {("NO_FACE"|"MULTIPLE_FACES"|"UNDER_16"|"NSFW"|"PHOTODNA_MATCH"|"MODEL_REFUSED"|"MODEL_MALFORMED"|"INTERNAL"|"QUOTA_EXHAUSTED")} RejectionCode
 *
 * @typedef {Object} RejectionResponse
 * @property {"rejected"} status
 * @property {RejectionCode} rejection_code
 * @property {string} rejection_message
 *
 * @typedef {VerdictResponse | RejectionResponse} OracleResponse
 */

import { CATEGORIES, SUBCATEGORIES } from "./taxonomy";

// LEGACY pre-taxonomy vectors — used ONLY to render frozen old scans + old glowups.
// New scans never emit these. Do not build new features on them.
export const LEGACY_VECTOR_NAMES = Object.freeze([
  "skin", "jaw", "brows", "eyes", "hair", "lips", "harmony", "skin_tone",
]);
export const LEGACY_VECTOR_DISPLAY_LABELS = Object.freeze({
  skin: "SKIN", jaw: "JAW", brows: "BROWS", eyes: "EYES",
  hair: "HAIR", lips: "LIPS", harmony: "HARMONY", skin_tone: "SKIN TONE",
});

// Universal display-label resolver: legacy vectors + taxonomy categories +
// subcategories, all uppercased. Components do `VECTOR_DISPLAY_LABELS[key] ||
// key.toUpperCase()`, so this single map serves old and new keys alike.
export const VECTOR_DISPLAY_LABELS = Object.freeze({
  ...LEGACY_VECTOR_DISPLAY_LABELS,
  ...CATEGORIES.reduce((acc, c) => { acc[c.key] = c.label.toUpperCase(); return acc; }, {}),
  ...SUBCATEGORIES.reduce((acc, s) => { acc[s.key] = s.label.toUpperCase(); return acc; }, {}),
});

// Canonical key list for the deterministic 2D geometry read (pipeline Stage 2).
export const GEOMETRY_METRIC_KEYS = Object.freeze([
  "thirds", "fifths", "canthal_tilt", "fwhr", "esr", "symmetry", "lip_ratio",
]);

export const REJECTION_CODES = Object.freeze({
  NO_FACE: "NO_FACE",
  MULTIPLE_FACES: "MULTIPLE_FACES",
  UNDER_16: "UNDER_16",
  NSFW: "NSFW",
  PHOTODNA_MATCH: "PHOTODNA_MATCH",
  MODEL_REFUSED: "MODEL_REFUSED",
  MODEL_MALFORMED: "MODEL_MALFORMED",
  INTERNAL: "INTERNAL",
  // Today's scan allowance is spent (1/day free, 5/day member). Not a content
  // rejection — the client parks in the terminal `quotaBlocked` phase showing
  // when the limit reopens, rather than rendering a rejection card.
  QUOTA_EXHAUSTED: "QUOTA_EXHAUSTED",
  // Signed-in caller has not confirmed their email address. Not a content
  // rejection — the client shows the confirm-email gate (resend + refresh),
  // not a rejection card. Blunts the throwaway-account free-scan sybil path.
  EMAIL_UNVERIFIED: "EMAIL_UNVERIFIED",
  // The caller's previous reading is still sealed. Scans are unlimited, but
  // only ONE may be outstanding at a time — a reading spends real inference
  // before it is sealed. Not a content rejection: the client parks in the
  // terminal `pendingUnlock` phase pointing at the way out (judge, or start a
  // trial) rather than rendering a rejection card.
  SCAN_PENDING_UNLOCK: "SCAN_PENDING_UNLOCK",
});

// Per-concern classification bands Gemini attaches to product-driving subcats
// (catalog Part 1.2). The model classifies; deterministic rules pick strength.
export const SEVERITY_BANDS = Object.freeze(["none", "mild", "moderate", "severe"]);

// How a routine item is delivered: a laddered PRODUCT, a CLINICIAN routing card
// (conditions, catalog Part 8), or BEHAVIORAL coaching (habits, catalog Part 6).
export const ROUTINE_ROUTES = Object.freeze({
  PRODUCT: "product",
  CLINICIAN: "clinician",
  BEHAVIORAL: "behavioral",
});

// Deterministic sample subcategory scores for fixtures (descending-ish spread).
const SAMPLE_SUB_SCORES = SUBCATEGORIES.map((s, i) => ({
  key: s.key,
  score: 55 + ((i * 7) % 40), // 55..94, stable per key index
  note: `Sample reading for ${s.label}.`,
}));

/**
 * Canonical sample VerdictResponse for tests, Storybook, and mocked dev mode.
 * @type {VerdictResponse}
 */
export const SAMPLE_VERDICT_RESPONSE = Object.freeze({
  status: "success",
  scan_id: "00000000-0000-0000-0000-000000000000",
  timestamp: "2026-06-06T00:00:00.000Z",
  processed_image_url: "/sample/processed.jpg",
  verdict: {
    overall_score: 78,
    overall_score_measured: 78,
    adherence_bonus: 0,
    categories: CATEGORIES.map((c, i) => ({
      key: c.key,
      score: 70 + ((i * 5) % 25),
      summary: `Sample guidance for ${c.label}.`,
    })),
    subcategories: SAMPLE_SUB_SCORES,
    top_improvements: ["acne", "tone", "hairline", "pores", "texture"],
  },
  routine: {
    am: [
      {
        active_id: "vitc", active: "Vitamin C", step: "CORRECT", use_time: "AM",
        concern: "SKIN · TONE", cls: "mod", treats: "Tone & pigment", timeline: "3-6 mo+",
        rung_name: "C derivative", rung_index: 0, rung_count: 4, at_ceiling: false, rx: false,
        product: "Vitamin C Complex Serum", brand: "Naturium", price: "$20",
        affiliate_url: "https://example.com/p", image_url: "", adherence: "yes",
        tags: ["Serums", "Brightening"], rating: 4.5, review_count: 8120,
      },
    ],
    pm: [
      {
        active_id: "retinoid", active: "Retinoid", step: "TREAT", use_time: "PM",
        concern: "SKIN · TEXTURE", cls: "mod", treats: "Texture & congestion", timeline: "1-3 mo+",
        rung_name: "Retinol 0.3%", rung_index: 1, rung_count: 5, at_ceiling: false, rx: false,
        product: "Retinol 0.3% in Squalane", brand: "The Ordinary", price: "$10",
        affiliate_url: "", image_url: "", adherence: "partial", route: "product",
      },
      {
        active_id: "clinician_acne", active: "Acne & breakouts", step: "CONSULT", use_time: "PM",
        concern: "SKIN · ACNE", cls: "mod", treats: "Active breakouts", timeline: "",
        rung_name: "", rung_index: 0, rung_count: 0, at_ceiling: false, rx: true,
        product: "", brand: "", price: "$0", affiliate_url: "", image_url: "", adherence: null,
        route: "clinician",
        coaching: "Deep, painful breakouts can read as something a professional should look at — a dermatologist can confirm the right approach for you.",
      },
      {
        active_id: "behavioral_facial_fat", active: "Definition", step: "HABIT", use_time: "PM",
        concern: "DEFINITION", cls: "mod", treats: "Facial definition", timeline: "1-3 mo+",
        rung_name: "", rung_index: 0, rung_count: 0, at_ceiling: false, rx: false,
        product: "", brand: "", price: "$0", affiliate_url: "", image_url: "", adherence: null,
        route: "behavioral",
        coaching: "This one responds to habits more than products — gradual leanness, sleep, and lower sodium tend to reveal more definition over time.",
      },
    ],
    total_price: "$30",
    stage: 2,
  },
  learned_percentile: 0.73,
  geometry: {
    metrics: [
      { key: "thirds", label: "FACIAL THIRDS", value: 0.02, score: 89, band: "strong" },
      { key: "symmetry", label: "SYMMETRY", value: 0.06, score: 42, band: "addressable" },
    ],
    overall: 66,
    strong: ["thirds"],
    addressable: ["symmetry"],
  },
});

/** @param {unknown} obj @returns {boolean} */
export function isVerdictResponse(obj) {
  return Boolean(
    obj && typeof obj === "object"
      && obj.status === "success"
      && obj.scan_id
      && obj.verdict,
  );
}

/** @param {unknown} obj @returns {boolean} */
export function isRejectionResponse(obj) {
  return Boolean(
    obj && typeof obj === "object"
      && obj.status === "rejected"
      && REJECTION_CODES[obj.rejection_code],
  );
}

/** True when a scan/verdict uses the new subcategory taxonomy (vs a frozen legacy scan). */
export function isTaxonomyVerdict(v) {
  return Boolean(v && typeof v === "object" && Array.isArray(v.subcategories));
}
